import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, PhoneCall, ChatText, MapPin, Trash, CheckSquare, Square, X, EnvelopeSimple, EnvelopeSimpleOpen, Checks } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";

const EVENT_META = {
  scan: { Icon: MagnifyingGlass, color: "#14b8a6", key: "eventScan" },
  call: { Icon: PhoneCall, color: "#3b82f6", key: "eventCall" },
  message: { Icon: ChatText, color: "#a855f7", key: "eventMessage" },
};

export function EventsManager({ events, showBuddy = false, onChange }) {
  const { t, lang } = useApp();
  const [items, setItems] = useState(events || []);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("date_desc");
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmIds, setConfirmIds] = useState(null);
  const pending = useRef({});
  const swipe = useRef({});

  useEffect(() => { setItems(events || []); }, [events]);

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(lang === "de" ? "de-AT" : "en-GB"); } catch { return iso || ""; } };
  const typeLabel = (typ) => t(EVENT_META[typ]?.key || "eventScan");

  const view = useMemo(() => {
    let arr = [...items];
    const s = q.trim().toLowerCase();
    if (s) {
      arr = arr.filter((e) =>
        (e.tag_label || "").toLowerCase().includes(s) ||
        (e.message || "").toLowerCase().includes(s) ||
        (e.finder_name || "").toLowerCase().includes(s) ||
        typeLabel(e.type).toLowerCase().includes(s) ||
        fmt(e.timestamp).toLowerCase().includes(s));
    }
    const byDate = (a, b) => new Date(a.timestamp) - new Date(b.timestamp);
    if (sort === "date_desc") arr.sort((a, b) => byDate(b, a));
    else if (sort === "date_asc") arr.sort(byDate);
    else if (sort === "buddy") arr.sort((a, b) => (a.tag_label || "").localeCompare(b.tag_label || ""));
    else if (sort === "type") arr.sort((a, b) => (a.type || "").localeCompare(b.type || ""));
    return arr;
  }, [items, q, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  const unreadCount = items.filter((e) => !e.read).length;

  const commitDelete = (ids) => {
    const idset = new Set(ids);
    const removed = items.filter((e) => idset.has(e.id));
    setItems((prev) => prev.filter((e) => !idset.has(e.id)));
    setSelected(new Set());
    setSelecting(false);
    let undone = false;
    const key = ids.join(",");
    const to = setTimeout(async () => {
      delete pending.current[key];
      try {
        if (ids.length === 1) await api.delete(`/events/${ids[0]}`);
        else await api.post("/events/delete", { ids });
        onChange && onChange();
      } catch { toast.error(t("deleteFailed")); setItems((prev) => [...removed, ...prev]); }
    }, 5000);
    pending.current[key] = to;
    toast(t("eventsDeleted", { n: ids.length }), {
      duration: 5000,
      action: { label: t("undo"), onClick: () => { if (undone) return; undone = true; clearTimeout(to); delete pending.current[key]; setItems((prev) => { const have = new Set(prev.map((x) => x.id)); return [...removed.filter((r) => !have.has(r.id)), ...prev]; }); } },
    });
  };

  const markRead = async (ids, read) => {
    setItems((prev) => prev.map((e) => (ids.includes(e.id) ? { ...e, read } : e)));
    try { await api.post("/events/read", { ids, read }); } catch { toast.error(t("deleteFailed")); }
  };
  const markAllRead = () => { const ids = view.filter((e) => !e.read).map((e) => e.id); if (ids.length) markRead(ids, true); };

  const askDelete = (ids) => setConfirmIds(ids);
  const doConfirmDelete = () => { if (confirmIds) commitDelete(confirmIds); setConfirmIds(null); };
  const toggleSel = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div data-testid="events-manager">
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="events-search" className="sb-input pl-9" placeholder={t("eventsSearchPlaceholder")} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select data-testid="events-sort" className="sb-input sm:w-52" value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="date_desc">{t("sortDateDesc")}</option>
          <option value="date_asc">{t("sortDateAsc")}</option>
          <option value="buddy">{t("sortByBuddy")}</option>
          <option value="type">{t("sortByType")}</option>
        </select>
      </div>

      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button data-testid="events-mark-all-read" disabled={unreadCount === 0} onClick={markAllRead} className="sb-btn px-3 py-1.5 bg-white text-sm font-medium flex items-center gap-1 disabled:opacity-40"><Checks size={15} weight="bold" style={{ color: "var(--app-strong)" }} />{t("eventsMarkAllRead")}</button>
          <button data-testid="events-delete-all" onClick={() => askDelete(view.map((e) => e.id))} className="sb-btn px-3 py-1.5 text-white text-sm font-medium flex items-center gap-1" style={{ background: "#EF4444" }}><Trash size={15} weight="bold" />{t("eventsDeleteAll")}</button>
        </div>
      )}

      <div className="flex items-center justify-between mb-2">
        <button data-testid="events-select-toggle" onClick={() => { setSelecting((s) => !s); setSelected(new Set()); }} className="font-body text-sm text-gray-600 flex items-center gap-1">
          {selecting ? <X size={16} /> : <CheckSquare size={16} />}{selecting ? t("cancel") : t("selectMultiple")}
        </button>
        {selecting && (
          <div className="flex items-center gap-2">
            <button data-testid="events-mark-selected-read" disabled={selected.size === 0} onClick={() => { markRead([...selected], true); setSelecting(false); setSelected(new Set()); }} className="sb-btn px-3 py-1.5 bg-white text-sm font-medium flex items-center gap-1 disabled:opacity-40"><Checks size={15} weight="bold" />({selected.size})</button>
            <button data-testid="events-delete-selected" disabled={selected.size === 0} onClick={() => askDelete([...selected])} className="sb-btn px-3 py-1.5 text-white text-sm font-medium flex items-center gap-1 disabled:opacity-40" style={{ background: "#EF4444" }}>
              <Trash size={15} weight="bold" />{t("deleteSelected")} ({selected.size})
            </button>
          </div>
        )}
      </div>

      {view.length === 0 ? (
        <p className="font-body text-gray-500" data-testid="events-empty">{t("noEvents")}</p>
      ) : (
        <div className="space-y-2">
          {view.map((ev) => {
            const meta = EVENT_META[ev.type] || EVENT_META.scan;
            const { Icon } = meta;
            const isSel = selected.has(ev.id);
            const unread = !ev.read;
            return (
              <div key={ev.id} className="sb-card p-3 flex items-center gap-3" data-testid={`event-row-${ev.id}`}
                data-unread={unread ? "1" : "0"}
                onTouchStart={(e) => { swipe.current[ev.id] = e.touches[0].clientX; }}
                onTouchEnd={(e) => { const st = swipe.current[ev.id]; if (st != null && e.changedTouches[0].clientX - st < -60) askDelete([ev.id]); }}>
                {selecting && (
                  <button data-testid={`event-select-${ev.id}`} onClick={() => toggleSel(ev.id)} className="shrink-0 text-gray-600">
                    {isSel ? <CheckSquare size={22} weight="fill" style={{ color: "var(--app-primary)" }} /> : <Square size={22} />}
                  </button>
                )}
                {unread && <span data-testid={`event-unread-dot-${ev.id}`} className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: "var(--app-strong)" }} />}
                <div className="p-2 rounded-xl shrink-0" style={{ background: meta.color, filter: `drop-shadow(0 0 5px ${meta.color}88)` }}><Icon size={18} weight="bold" color="#fff" /></div>
                <div className="flex-1 min-w-0">
                  <p className={`font-heading text-gray-900 ${unread ? "font-bold" : "font-medium"}`}>{t(meta.key)}{showBuddy && ev.tag_label ? <span className="font-body text-gray-500"> · {ev.tag_label}</span> : null}</p>
                  {ev.type === "message" && ev.message && <p className="text-sm font-body text-gray-600 truncate">"{ev.message}" — {ev.finder_name || t("unnamed")}</p>}
                  {ev.type === "message" && ev.lat != null && ev.lng != null && (
                    <a data-testid="event-location-link" href={`https://www.google.com/maps?q=${ev.lat},${ev.lng}`} target="_blank" rel="noreferrer" className="text-xs font-heading font-medium underline inline-flex items-center gap-1 mt-1" style={{ color: "var(--app-strong)" }}><MapPin size={13} weight="fill" />{t("viewLocation")}</a>
                  )}
                  <p className="text-xs font-body text-gray-400 mt-0.5">{fmt(ev.timestamp)}</p>
                </div>
                {!selecting && (
                  <div className="flex items-center shrink-0">
                    <button data-testid={`event-read-${ev.id}`} onClick={() => markRead([ev.id], unread)} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 transition-colors" title={unread ? t("markRead") : t("markUnread")}>
                      {unread ? <EnvelopeSimple size={18} weight="bold" /> : <EnvelopeSimpleOpen size={18} weight="bold" />}
                    </button>
                    <button data-testid={`event-delete-${ev.id}`} onClick={() => askDelete([ev.id])} className="p-2 rounded-lg text-gray-400 hover:text-red-500 transition-colors" title={t("delete")}><Trash size={18} weight="bold" /></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!confirmIds} onOpenChange={(o) => !o && setConfirmIds(null)}>
        <AlertDialogContent data-testid="events-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("eventsDeleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("eventsDeleteConfirmDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="events-delete-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="events-delete-ok" onClick={doConfirmDelete} style={{ background: "#EF4444" }}>{t("delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
